import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {GradesState} from './types'
import {
  createGrade,
  updateGrade,
  deleteGrade,
  approveGrade,
  rejectGrade,
  fetchGrades,
  fetchGradeById,
  createTabulation,
  updateTabulation,
  deleteTabulation,
  submitTabulation,
  fetchTabulations,
  fetchTabulationById,
  fetchApprovalQueue,
  fetchSubmissionDetails,
  detectMissingGrades,
  reviewTabulation,
  fetchBulletin,
  fetchStudentGrades,
} from './service'
import {
  CreateGradeResponse,
  UpdateGradeResponse,
  FetchGradesResponse,
  CreateTabulationResponse,
  UpdateTabulationResponse,
  DeleteTabulationResponse,
  SubmitTabulationResponse,
  FetchTabulationsResponse,
  ReviewTabulationResponse,
  FetchBulletinResponse,
  FetchBulletinsResponse,
  MissingGradesResponse,
  FetchSubmissionDetailsResponse,
  FetchStudentGradesResponse,
  StudentGrade,
} from './types'

const initialState: GradesState = {
  tabulations: [],
  tabulationForEdit: null,
  queueItems: [],
  gradeEntries: [],
  grades: [],
  bulletins: {},
  currentBulletin: null,
  studentGrades: [],
  currentStudentGrades: null,
  missingGrades: null,
  listLoading: false,
  actionLoading: false,
  error: null,
  total: 0,
  totalPages: 0,
}

const gradesSlice = createSlice({
  name: 'grades',
  initialState,
  reducers: {
    resetGrades: (state: GradesState) => {
      state.tabulations = []
      state.grades = []
      state.gradeEntries = []
      state.queueItems = []
      state.total = 0
      state.totalPages = 0
      state.missingGrades = null
      state.currentBulletin = null
      state.currentStudentGrades = null
      state.studentGrades = []
    },
    clearError: (state: GradesState) => {
      state.error = null
    },
    clearMissingGrades: (state: GradesState) => {
      state.missingGrades = null
    },
  },
  extraReducers: (builder) => {
    // ==================== GRADE ACTIONS ====================

    builder.addCase(fetchGrades.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchGrades.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch grades')
    })
    builder.addCase(fetchGrades.fulfilled, (state: GradesState, action: PayloadAction<FetchGradesResponse>) => {
      state.listLoading = false
      state.error = null
      state.grades = action.payload.grades || []
      state.total = action.payload.total || 0
      state.totalPages = action.payload.totalPages || 0
    })

    builder.addCase(fetchGradeById.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchGradeById.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch grade')
    })
    builder.addCase(fetchGradeById.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = null
      // Update or add grade to list
      const index = state.grades.findIndex((g) => g._id === action.payload.grade._id)
      if (index !== -1) {
        state.grades[index] = action.payload.grade
      } else {
        state.grades.push(action.payload.grade)
      }
    })

    builder.addCase(createGrade.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createGrade.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to create grade')
    })
    builder.addCase(createGrade.fulfilled, (state: GradesState, action: PayloadAction<CreateGradeResponse>) => {
      state.actionLoading = false
      state.error = null
      if (action.payload.grade) {
        state.grades.unshift(action.payload.grade)
      }
    })

    builder.addCase(updateGrade.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updateGrade.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to update grade')
    })
    builder.addCase(updateGrade.fulfilled, (state: GradesState, action: PayloadAction<UpdateGradeResponse>) => {
      state.actionLoading = false
      state.error = null
      if (action.payload.grade) {
        const index = state.grades.findIndex((g) => g._id === action.payload.grade._id)
        if (index !== -1) {
          state.grades[index] = action.payload.grade
        }
      }
    })

    builder.addCase(deleteGrade.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deleteGrade.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to delete grade')
    })
    builder.addCase(deleteGrade.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = null
      state.grades = state.grades.filter((g) => g._id !== action.payload.id)
    })

    builder.addCase(approveGrade.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(approveGrade.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to approve grade')
    })
    builder.addCase(approveGrade.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = null
      if (action.payload.grade) {
        const index = state.grades.findIndex((g) => g._id === action.payload.grade._id)
        if (index !== -1) {
          state.grades[index] = action.payload.grade
        }
      }
    })

    builder.addCase(rejectGrade.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(rejectGrade.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to reject grade')
    })
    builder.addCase(rejectGrade.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = null
      if (action.payload.grade) {
        const index = state.grades.findIndex((g) => g._id === action.payload.grade._id)
        if (index !== -1) {
          state.grades[index] = action.payload.grade
        }
      }
    })

    // ==================== TABULATION ACTIONS ====================

    builder.addCase(fetchTabulations.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchTabulations.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      const errorPayload = action.payload
      let errorMessage = 'Failed to fetch tabulations'
      
      // Handle 400 error with 'Invalid gradeId' message
      if (errorPayload?.error) {
        if (typeof errorPayload.error === 'string') {
          errorMessage = errorPayload.error
        } else if (errorPayload.error.message) {
          errorMessage = errorPayload.error.message
        }
      }
      
      state.error = errorMessage
      console.error('❌ Redux: fetchTabulations.rejected', {
        error: errorMessage,
        payload: errorPayload
      })
    })
    builder.addCase(fetchTabulations.fulfilled, (state: GradesState, action: PayloadAction<FetchTabulationsResponse>) => {
      state.listLoading = false
      state.error = null
      const newTabulations = action.payload.tabulations || []
      console.log('✅ Redux: fetchTabulations.fulfilled', {
        receivedCount: newTabulations.length,
        tabulations: newTabulations
      })
      // Replace the array with new results (this is expected behavior when filtering by status)
      state.tabulations = newTabulations
      state.total = action.payload.total || 0
      state.totalPages = action.payload.totalPages || 0
    })

    builder.addCase(fetchTabulationById.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchTabulationById.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch tabulation')
    })
    builder.addCase(fetchTabulationById.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = null
      state.tabulationForEdit = action.payload.tabulation
    })

    builder.addCase(createTabulation.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createTabulation.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to create tabulation')
    })
    builder.addCase(createTabulation.fulfilled, (state: GradesState, action: PayloadAction<CreateTabulationResponse>) => {
      state.actionLoading = false
      state.error = null
      state.tabulationForEdit = action.payload.tabulation
      // Add to list if not already present
      if (!state.tabulations.find((t) => t._id === action.payload.tabulation._id)) {
        state.tabulations.unshift(action.payload.tabulation)
      }
    })

    builder.addCase(updateTabulation.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updateTabulation.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to update tabulation')
    })
    builder.addCase(updateTabulation.fulfilled, (state: GradesState, action: PayloadAction<UpdateTabulationResponse>) => {
      state.actionLoading = false
      state.error = null
      // Update tabulationForEdit if it matches
      if (state.tabulationForEdit && state.tabulationForEdit.grades) {
        // Keep existing structure but update grade count if available
        // The actual grades are updated via the payload but we don't have the full tabulation here
      }
    })

    builder.addCase(deleteTabulation.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deleteTabulation.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to delete tabulation')
    })
    builder.addCase(deleteTabulation.fulfilled, (state: GradesState, action: PayloadAction<DeleteTabulationResponse>) => {
      state.actionLoading = false
      state.error = null
      // The tabulation will be removed from the list when we refetch
    })

    builder.addCase(submitTabulation.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(submitTabulation.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      // Handle missing grades error (409) specially
      if (action.payload?.status === 409) {
        // Store missing grades info
        state.missingGrades = {
          tabulationId: '',
          totalStudents: 0,
          requiredExamTypes: [],
          missingCount: action.payload.missingCount || 0,
          missingGrades: action.payload.missingGrades || [],
          isComplete: false,
          summary: action.payload.summary || {totalRequired: 0, totalEntered: 0, totalMissing: 0},
        }
      }
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to submit tabulation')
    })
    builder.addCase(submitTabulation.fulfilled, (state: GradesState, action: PayloadAction<SubmitTabulationResponse>) => {
      state.actionLoading = false
      state.error = null
      state.missingGrades = null
      // Update tabulation status in list
      if (state.tabulationForEdit) {
        state.tabulationForEdit.status = 'pending_approval'
        state.tabulationForEdit.submissionId = action.payload.submissionId
      }
      // Update in list
      const index = state.tabulations.findIndex((t) => t._id === state.tabulationForEdit?._id)
      if (index !== -1) {
        state.tabulations[index] = {...state.tabulations[index], status: 'pending_approval', submissionId: action.payload.submissionId}
      }
    })

    builder.addCase(fetchApprovalQueue.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchApprovalQueue.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch approval queue')
    })
    builder.addCase(fetchApprovalQueue.fulfilled, (state: GradesState, action: PayloadAction<FetchTabulationsResponse>) => {
      state.listLoading = false
      state.error = null
      state.queueItems = action.payload.tabulations || []
      state.total = action.payload.total || 0
      state.totalPages = action.payload.totalPages || 0
    })

    builder.addCase(fetchSubmissionDetails.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchSubmissionDetails.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch submission details')
    })
    builder.addCase(fetchSubmissionDetails.fulfilled, (state: GradesState, action: PayloadAction<FetchSubmissionDetailsResponse>) => {
      state.listLoading = false
      state.error = null
      state.tabulationForEdit = action.payload.submission.tabulation
      state.gradeEntries = action.payload.submission.gradeEntries || []
      if (action.payload.submission.missingGrades) {
        state.missingGrades = action.payload.submission.missingGrades
      }
    })

    builder.addCase(detectMissingGrades.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(detectMissingGrades.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to detect missing grades')
    })
    builder.addCase(detectMissingGrades.fulfilled, (state: GradesState, action: PayloadAction<MissingGradesResponse>) => {
      state.listLoading = false
      state.error = null
      state.missingGrades = action.payload
    })

    builder.addCase(reviewTabulation.pending, (state: GradesState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(reviewTabulation.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to review tabulation')
    })
    builder.addCase(reviewTabulation.fulfilled, (state: GradesState, action: PayloadAction<ReviewTabulationResponse>) => {
      state.actionLoading = false
      state.error = null
      // Update tabulation status
      if (state.tabulationForEdit && state.tabulationForEdit._id === action.payload.tabulation._id) {
        state.tabulationForEdit = action.payload.tabulation
      }
      // Update in list
      const index = state.tabulations.findIndex((t) => t._id === action.payload.tabulation._id)
      if (index !== -1) {
        state.tabulations[index] = action.payload.tabulation
      }
      // Remove from queue if approved/rejected
      if (action.payload.tabulation.status === 'approved' || action.payload.tabulation.status === 'rejected') {
        state.queueItems = state.queueItems.filter((q) => q._id !== action.payload.tabulation._id)
      }
    })

    // ==================== BULLETIN ACTIONS ====================

    builder.addCase(fetchBulletin.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchBulletin.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch bulletin')
    })
    builder.addCase(fetchBulletin.fulfilled, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = null
      
      // Handle single term bulletin
      if (action.payload.bulletin && action.payload.term) {
        state.currentBulletin = action.payload.bulletin
        const studentId = action.payload.bulletin.student?._id || action.payload.bulletin.student
        if (studentId && typeof studentId === 'string') {
          if (!state.bulletins[studentId]) {
            state.bulletins[studentId] = {}
          }
          state.bulletins[studentId][action.payload.term as 'T1' | 'T2' | 'T3'] = action.payload.bulletin
        }
      }
      
      // Handle all terms bulletins
      if (action.payload.bulletins && action.payload.studentId) {
        state.bulletins[action.payload.studentId] = action.payload.bulletins
      }
    })

    // ==================== STUDENT GRADES ACTIONS ====================

    builder.addCase(fetchStudentGrades.pending, (state: GradesState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchStudentGrades.rejected, (state: GradesState, action: PayloadAction<any>) => {
      state.listLoading = false
      state.error = typeof action.payload?.error === 'string' ? action.payload.error : String(action.payload?.error || 'Failed to fetch student grades')
    })
    builder.addCase(fetchStudentGrades.fulfilled, (state: GradesState, action: PayloadAction<FetchStudentGradesResponse>) => {
      state.listLoading = false
      state.error = null
      state.currentStudentGrades = action.payload.grades
      state.studentGrades = action.payload.grades
    })
  },
})

export default gradesSlice.reducer
export const {resetGrades, clearError, clearMissingGrades} = gradesSlice.actions
